<template>
  <Container class="gap-10 md:gap-15 2xl:gap-17.5">
    <div
      class="relative mx-auto grid aspect-square w-[277.461px] place-items-center sm:w-[308.539px] md:w-[519.792px] lg:w-[616px] xl:w-[740px] min-[1680px]:w-[866px]"
    >
      <h1
        class="bp1680:w-[1600px] absolute top-1/2 left-1/2 z-20 m-0 w-[335px] -translate-x-1/2 -translate-y-1/2 text-center text-xl/[23px] font-medium text-black select-none sm:w-[440px] md:w-[728px] md:text-4.5xl lg:w-[857px] lg:text-[50px] xl:w-[1360px] xl:text-6.5xl 2xl:w-[1600px] 2xl:text-[80px]/[80px]!"
      >
        {{ t('homepage.full_title') }}
      </h1>

      <div
        v-for="(image, index) in images"
        :key="index"
        :ref="
          (el) => {
            if (el) slideRefs[index] = el as HTMLDivElement
          }
        "
        class="col-start-1 row-start-1 flex size-full [transform:translateZ(0)] items-center justify-center self-stretch [backface-visibility:hidden]"
        :style="
          index === 0
            ? {
                zIndex: 10,
                clipPath: 'inset(-5% -5% -5% -5%)',
                opacity: 1,
                visibility: 'visible',
              }
            : {
                zIndex: 1,
                clipPath: 'inset(-5% -5% -5% 105%)',
                opacity: 0,
                visibility: 'hidden',
              }
        "
      >
        <NuxtImg
          :src="image.url"
          class="block aspect-square size-full [transform:translateZ(0)] object-contain drop-shadow-[0_2.504px_2.504px_rgba(0,0,0,0.25)] sm:drop-shadow-[0_2.252px_2.252px_rgba(0,0,0,0.25)] md:w-auto md:max-w-full md:drop-shadow-[0_4.221px_4.221px_rgba(0,0,0,0.25)] lg:drop-shadow-[0_5px_5px_rgba(0,0,0,0.25)] min-[1680px]:drop-shadow-[0_6px_6px_rgba(0,0,0,0.25)]"
          :preload="index === 0 ? { fetchPriority: 'high' } : undefined"
          :alt="`hero image ${index + 1}`"
          format="webp"
          quality="80"
          sizes="xs:280px sm:310px md:520px lg:620px xl:740px xxl:870px"
        />
      </div>
    </div>

    <div
      v-if="buttons"
      class="mx-auto flex flex-col sm:w-110 md:w-182 lg:mx-0 lg:w-full"
    >
      <Button
        v-for="(button, index) in buttons"
        :key="index"
        :title="button.title"
        :aria-label="button.title + ' button'"
        :href="localePath(button.url)"
        :color="button.color"
        :target="button.target"
        :is-download="button.type === 'file'"
        class="w-full first:border-b-0 lg:mx-auto lg:w-214.5 xl:w-223.25 2xl:w-263.5"
      />
    </div>
  </Container>
</template>

<script setup lang="ts">
import { gsap } from 'gsap'
import type { CtaButton, Media } from '~/types'

interface Props {
  images: Media[]
  buttons: CtaButton[]
}
const props = defineProps<Props>()

const { t } = useI18n()
const localePath = useLocalePath()
const slideRefs = ref<HTMLDivElement[]>([])
let timeline: gsap.Timeline | null = null

const PAD = -5
const HIDE = 105
const getActiveZIndex = (index: number) => (index % 2 !== 0 ? 30 : 10)

const clip = (t: number, r: number, b: number, l: number) =>
  `inset(${t}% ${r}% ${b}% ${l}%)`

const SHOW = clip(PAD, PAD, PAD, PAD)
const HIDDEN = clip(PAD, PAD, PAD, HIDE)

const HOLD = 1.5
const DURATION = 1.5

const startAnimation = () => {
  if (!props.images || props.images.length < 2) return

  timeline?.kill()
  timeline = gsap.timeline({ repeat: -1 })

  const slides = slideRefs.value.slice(0, props.images.length)

  slides.forEach((slide, i) => {
    gsap.set(slide, {
      zIndex: i === 0 ? getActiveZIndex(0) : 1,
      clipPath: i === 0 ? SHOW : HIDDEN,
      autoAlpha: i === 0 ? 1 : 0,
    })
  })

  slides.forEach((current, i) => {
    const nextIndex = (i + 1) % slides.length
    const next = slides[nextIndex]!
    const horizontal = i % 2 === 0

    const nextFrom = horizontal
      ? clip(PAD, PAD, PAD, HIDE)
      : clip(HIDE, PAD, PAD, PAD)
    const currentTo = horizontal
      ? clip(PAD, HIDE, PAD, PAD)
      : clip(PAD, PAD, HIDE, PAD)

    const currentZ = getActiveZIndex(i)
    const nextZ = getActiveZIndex(nextIndex)
    const nextAnimZ = nextZ === currentZ ? nextZ + 1 : nextZ

    const label = `transition-${i}`
    timeline!.addLabel(label, `+=${HOLD}`)

    timeline!.set(current, { zIndex: currentZ, autoAlpha: 1 }, label)
    timeline!.set(next, { zIndex: nextAnimZ, autoAlpha: 1 }, label)

    timeline!.fromTo(
      next,
      { clipPath: nextFrom },
      {
        clipPath: SHOW,
        duration: DURATION,
        ease: 'power2.inOut',
        immediateRender: false,
      },
      label,
    )
    timeline!.fromTo(
      current,
      { clipPath: SHOW },
      {
        clipPath: currentTo,
        duration: DURATION,
        ease: 'power2.inOut',
        immediateRender: false,
      },
      label,
    )

    timeline!.set(
      current,
      { zIndex: 1, clipPath: HIDDEN, autoAlpha: 0 },
      `${label}+=${DURATION}`,
    )
    timeline!.set(
      next,
      { zIndex: nextZ, autoAlpha: 1 },
      `${label}+=${DURATION}`,
    )
  })
}

watch(
  () => props.images,
  (newImages) => {
    if (newImages && newImages.length >= 2) {
      nextTick(() => {
        setTimeout(() => {
          startAnimation()
        }, 300)
      })
    }
  },
  { immediate: true },
)

onUnmounted(() => {
  timeline?.kill()
  timeline = null
})
</script>
